import React, { useState } from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, Tabs, Tab } from "@mui/material";
import { X } from "lucide-react";
import { externalApiTypes } from "../../../../Config/constants";
import { useTranslation } from "react-i18next";

export default function HowToUseAPIDialog({ dialogOpen, closeDialog, apiTypeId, apiUrl, apiBodyItems, ...props }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const excludeStopwatchKeys = ["stopwatchReference", "startTime", "stopTime", "singleTimer"];
  const excludeNumericKeys = ["keyValue", "measurementTime", "value"];

  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [copiedPd, setCopiedPd] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  // Format bodyItems
  const bodyItems = apiBodyItems.reduce((acc, item, index) => {
    acc[item.itemName] = `value ${index + 1}`;
    return acc;
  }, {});

  // Example code
  const jsCodeDataAPI = `fetch('${apiUrl}', {
method: 'POST',
headers: {
    'Content-Type': 'application/json',
},
body: JSON.stringify(
    {
        "keyValue": "123ABC", // optional key value (can be used to link to an order number)
        "measurementTime": "14/01/2025 13:30:12", // optional, if null the measurement will be logged at the current time
        "customReference": "abc-123987", // Optional: if you want to provide your own reference, use this
        // "priceBundleCode": "BUNDLE-A", // Optional: override the price bundle for this measurement
        // "priceDefinitions": [ { "code": "LABOR-01", "multiplier": 1 } ], // Optional: attach per-measurement pricing (see Price Definitions tab) ${Object.entries(bodyItems)
          .map(([key, value]) => `        "${key}": "${value}"`)
          .join(",\n")}
    }
)
})
.then(response => response.json())
.then(data => {
    console.log('Server response:', data);
})
.catch(error => {
    console.error('An error has occured:', error);
});`;

  const jsCodeStopwatchAPI = `fetch('${apiUrl}', {
method: 'POST',
headers: {
    'Content-Type': 'application/json',
},
body: JSON.stringify(
    {
        "stopwatchReference": "00000000-0000-0000-0000-000000000000", // if you are starting a new stopwatch, this should be left empty
        "customReference": "ORD-1001", // Optional: if you want to provide your own reference, use this
        "startTime": "14/01/2025 13:30:12", // the time you want to start the stopwatch
        "stopTime": "14/01/2025 13:42:41", // the time you want to stop the stopwatch (should always be later then startTime)
        "useRefAsKey": "True" // or false, will use reference as a key for the measurement (defaults to false) 
        "singleTimer": "True", // or false, when true starting a new timer will stop other running timers on this API
        "keyValue": "123ABC", // optional key value (can be used to link to an order number). If you use useRefAsKey, this shouldn't be used
        // "priceBundleCode": "BUNDLE-A", // Optional: override the price bundle for this measurement
        // "priceDefinitions": [ { "code": "LABOR-01", "multiplier": 1 }, { "code": "SETUP-01", "multiplier": 1, "isFixed": true } ], // Optional: attach per-measurement pricing (see Price Definitions tab) ${Object.entries(bodyItems)
          .filter(([key]) => !excludeStopwatchKeys.includes(key))
          .map(([key, value]) => `        "${key}": "${value}"`)
          .join(",\n")}
    }
)
})
.then(response => response.json())
.then(data => {
    console.log('Server response:', data);
})
.catch(error => {
    console.error('An error has occured:', error);
});`;

  const jsCodeNumericAPI = `fetch('${apiUrl}', {
method: 'POST',
headers: {
    'Content-Type': 'application/json',
},
body: JSON.stringify(
    {
        "value": "42", // the numeric value you want to measure (can be negative)
        "customReference": "ORD-1001", // Optional: if you want to provide your own reference, use this
        "measurementTime": "14/01/2025 13:30:12", // optional, if null the measurement will be logged at the current time
        "keyValue": "123ABC", // optional key value (can be used to link to an order number)
        // "priceBundleCode": "BUNDLE-A", // Optional: override the price bundle for this measurement
        // "priceDefinitions": [ { "code": "LABOR-01", "multiplier": 1 } ], // Optional: attach per-measurement pricing (see Price Definitions tab) ${Object.entries(bodyItems)
          .filter(([key]) => !excludeNumericKeys.includes(key))
          .map(([key, value]) => `        "${key}": "${value}"`)
          .join(",\n")}
    }
)
})
.then(response => response.json())
.then(data => {
    console.log('Server response:', data);
})
.catch(error => {
    console.error('An error has occured:', error);
});`;

  const priceDefinitionsCode = `// ── Option A: override the price bundle for the measurement ──────────────────
// Use "priceBundleCode" to switch which bundle is applied (e.g. project-specific
// or customer-specific bundles). The code maps to PriceBundle.Reference in Taplr.

{
    // ... your normal fields ...
    "priceBundleCode": "BUNDLE-A"   // Code of the Price Bundle (set in Taplr)
}

// ── Option B: attach individual price definitions ─────────────────────────
// Add "priceDefinitions" for fine-grained per-measurement pricing.
// When present, this takes full priority and the Price Bundle is ignored.

{
    // ... your normal fields ...
    "priceDefinitions": [
        {
            "code": "LABOR-01",       // Code of the Price Definition (set in Taplr)
            "multiplier": 2.5         // Quantity, hours, weight, etc.
        },
        {
            "code": "MATERIAL-STD",   // You can attach multiple price definitions
            "multiplier": 1           // Multiplier of 1 = unit price as-is
        },
        {
            "code": "SETUP-01",       // Fixed cost: applied once per completed measurement
            "multiplier": 1,          // instead of scaled by the stopwatch duration
            "isFixed": true           // Optional, defaults to false. Stopwatch APIs only
        }
    ]
}`;

  const copyCodeToClipboard = () => {
    navigator.clipboard
      .writeText(apiTypeId === externalApiTypes.DATA ? jsCodeDataAPI : apiTypeId === externalApiTypes.STOPWATCH ? jsCodeStopwatchAPI : jsCodeNumericAPI)
      .then(() => {
        setCopiedToClipboard(true);
        setTimeout(() => setCopiedToClipboard(false), 2000);
      })
      .catch((err) => console.error("Failed to copy link: ", err));
  };

  const copyPdCodeToClipboard = () => {
    navigator.clipboard
      .writeText(priceDefinitionsCode)
      .then(() => {
        setCopiedPd(true);
        setTimeout(() => setCopiedPd(false), 2000);
      })
      .catch((err) => console.error("Failed to copy link: ", err));
  };

  return (
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" onClose={closeDialog} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
          {t('howToUseApiDialog.title')}
        </DialogTitle>
        <button onClick={() => closeDialog()} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>

      <div className={classes.dialog__tabsContainer}>
        <Tabs
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v)}
          classes={{ indicator: classes.tabIndicator }}
        >
          <Tab label={t('howToUseApiDialog.tabFields')} className={classes.tab} disableRipple />
          <Tab label={t('howToUseApiDialog.tabCodeExample')} className={classes.tab} disableRipple />
          <Tab label={t('howToUseApiDialog.tabPriceDefinitions')} className={classes.tab} disableRipple />
        </Tabs>
      </div>

      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>

          {/* ── Tab 0: Fields ── */}
          {selectedTab === 0 && (
            <>
              {/* Data API */}
              {apiTypeId === externalApiTypes.DATA && (
                <div className={classes.dialog__textContainer}>
                  <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.dataApiIntro1')}</p>
                  <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.dataApiIntro2')}</p>

                  <h3 className={classes.textContainer__subtitle}>measurementTime</h3>
                  <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.measurementTimeDesc')}</p>

                  <h3 className={classes.textContainer__subtitle}>customReference</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.customReferenceDesc')}
                  </p>

                  <h3 className={classes.textContainer__subtitle}>priceBundleCode</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.priceBundleCodeDesc1')} <strong>{t('howToUseApiDialog.reference')}</strong> {t('howToUseApiDialog.priceBundleCodeDesc2')}
                    {t('howToUseApiDialog.priceBundleCodeDesc3')} <strong>{t('howToUseApiDialog.tabPriceDefinitions')}</strong> {t('howToUseApiDialog.priceBundleCodeDesc4')}
                  </p>
                </div>
              )}
              {/* Stopwatch API */}
              {apiTypeId === externalApiTypes.STOPWATCH && (
                <div className={classes.dialog__textContainer}>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.stopwatchApiIntro')}
                  </p>
                  <h3 className={classes.textContainer__subtitle}>Reference</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.referenceDesc')}
                  </p>
                  <p className={classes.textContainer__listTitle}>{t('howToUseApiDialog.referenceOptions')}</p>
                  <ul>
                    <li className={classes.textContainer__listItem}>
                      {t('howToUseApiDialog.referenceOption1')}
                    </li>
                    <li className={classes.textContainer__listItem}>
                      {t('howToUseApiDialog.referenceOption2')}
                    </li>
                  </ul>
                  <h3 className={classes.textContainer__subtitle}>startTime</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.startTimeDesc')}
                  </p>
                  <h3 className={classes.textContainer__subtitle}>stopTime</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.stopTimeDesc')}
                  </p>
                  <h3 className={classes.textContainer__subtitle}>keyValue</h3>
                  <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.keyValueDesc')}</p>
                  <h3 className={classes.textContainer__subtitle}>useRefAsKey</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.useRefAsKeyDesc')}
                  </p>
                  <h3 className={classes.textContainer__subtitle}>singleTimer</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.singleTimerDesc')}
                  </p>

                  <h3 className={classes.textContainer__subtitle}>priceBundleCode</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.priceBundleCodeDesc1')} <strong>{t('howToUseApiDialog.reference')}</strong> {t('howToUseApiDialog.priceBundleCodeDesc2')}
                    {t('howToUseApiDialog.priceBundleCodeDesc3')} <strong>{t('howToUseApiDialog.tabPriceDefinitions')}</strong> {t('howToUseApiDialog.priceBundleCodeDesc4')}
                  </p>
                </div>
              )}
              {/* Numeric API */}
              {apiTypeId === externalApiTypes.NUMERIC && (
                <div className={classes.dialog__textContainer}>
                  <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.numericApiIntro')}</p>

                  <h3 className={classes.textContainer__subtitle}>value</h3>
                  <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.numericValueDesc')}</p>

                  <h3 className={classes.textContainer__subtitle}>measurementTime</h3>
                  <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.measurementTimeDesc')}</p>

                  <h3 className={classes.textContainer__subtitle}>customReference</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.customReferenceDesc')}
                  </p>

                  <h3 className={classes.textContainer__subtitle}>keyValue</h3>
                  <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.numericKeyValueDesc')}</p>

                  <h3 className={classes.textContainer__subtitle}>priceBundleCode</h3>
                  <p className={classes.textContainer__paragraph}>
                    {t('howToUseApiDialog.priceBundleCodeDesc1')} <strong>{t('howToUseApiDialog.reference')}</strong> {t('howToUseApiDialog.priceBundleCodeDesc2')}
                    {t('howToUseApiDialog.priceBundleCodeDesc3')} <strong>{t('howToUseApiDialog.tabPriceDefinitions')}</strong> {t('howToUseApiDialog.priceBundleCodeDesc4')}
                  </p>
                </div>
              )}
            </>
          )}

          {/* ── Tab 1: Code example ── */}
          {selectedTab === 1 && (
            <div className={classes.functionExampleContainer}>
              <button className={classes.copyButton} onClick={copyCodeToClipboard}>
                {copiedToClipboard ? <p className={classes.copiedText}>{t('common.copied')}</p> : ""}
                <p className={`${copiedToClipboard ? classes.copied : ""}`}> {t('howToUseApiDialog.copyCode')}</p>
              </button>
              <SyntaxHighlighter
                language="javascript"
                customStyle={{
                  background: "transparent",
                  padding: "16px",
                  margin: "0",
                  paddingBottom: "8px",
                  paddingTop: "24px",
                }}
              >
                {apiTypeId === externalApiTypes.DATA ? jsCodeDataAPI : apiTypeId === externalApiTypes.STOPWATCH ? jsCodeStopwatchAPI : jsCodeNumericAPI}
              </SyntaxHighlighter>
            </div>
          )}

          {/* ── Tab 2: Price definitions ── */}
          {selectedTab === 2 && (
            <>
              <div className={classes.dialog__textContainer}>
                <p className={classes.textContainer__paragraph}>
                  {t('howToUseApiDialog.pdIntro')}
                </p>

                <h3 className={classes.textContainer__subtitle}>{t('howToUseApiDialog.pdOptionATitle')}</h3>
                <p className={classes.textContainer__paragraph}>
                  {t('howToUseApiDialog.pdOptionADesc1')} <code className={classes.inlineCode}>priceBundleCode</code> {t('howToUseApiDialog.pdOptionADesc2')}{" "}
                  <strong>{t('howToUseApiDialog.reference')}</strong> {t('howToUseApiDialog.pdOptionADesc3')}
                </p>

                <h3 className={classes.textContainer__subtitle}>{t('howToUseApiDialog.pdOptionBTitle')}</h3>
                <p className={classes.textContainer__paragraph}>
                  {t('howToUseApiDialog.pdOptionBDesc1')} <code className={classes.inlineCode}>priceDefinitions</code> {t('howToUseApiDialog.pdOptionBDesc2')}
                </p>
                <p className={classes.textContainer__paragraph}>{t('howToUseApiDialog.pdArrayFields')}</p>

                <h3 className={classes.textContainer__subtitle}>code</h3>
                <p className={classes.textContainer__paragraph}>
                  {t('howToUseApiDialog.pdCodeDesc1')} <code className={classes.inlineCode}>"LABOR-01"</code>{t('howToUseApiDialog.pdCodeDesc2')}
                </p>

                <h3 className={classes.textContainer__subtitle}>multiplier</h3>
                <p className={classes.textContainer__paragraph}>
                  {t('howToUseApiDialog.pdMultiplierDesc1')} <code className={classes.inlineCode}>1</code> {t('howToUseApiDialog.pdMultiplierDesc2')}
                </p>

                <h3 className={classes.textContainer__subtitle}>isFixed</h3>
                <p className={classes.textContainer__paragraph}>
                  {t('howToUseApiDialog.pdIsFixedDesc1')} <code className={classes.inlineCode}>false</code>{t('howToUseApiDialog.pdIsFixedDesc2')}
                </p>

                <p className={classes.textContainer__listTitle}>{t('howToUseApiDialog.pdKeepInMind')}</p>
                <ul>
                  <li className={classes.textContainer__listItem}>{t('howToUseApiDialog.pdNote1')}</li>
                  <li className={classes.textContainer__listItem}>
                    {t('howToUseApiDialog.pdNote2Pre')} <code className={classes.inlineCode}>priceDefinitions</code> {t('howToUseApiDialog.pdNote2Mid')} <code className={classes.inlineCode}>priceBundleCode</code> {t('howToUseApiDialog.pdNote2Post')}
                  </li>
                  <li className={classes.textContainer__listItem}>
                    {t('howToUseApiDialog.pdNote3')}
                  </li>
                  <li className={classes.textContainer__listItem}>
                    {t('howToUseApiDialog.pdNote4')}
                  </li>
                </ul>
              </div>

              <div className={classes.functionExampleContainer}>
                <button className={classes.copyButton} onClick={copyPdCodeToClipboard}>
                  {copiedPd ? <p className={classes.copiedText}>{t('common.copied')}</p> : ""}
                  <p className={`${copiedPd ? classes.copied : ""}`}> {t('howToUseApiDialog.copyCode')}</p>
                </button>
                <SyntaxHighlighter
                  language="javascript"
                  customStyle={{
                    background: "transparent",
                    padding: "16px",
                    margin: "0",
                    paddingBottom: "8px",
                    paddingTop: "24px",
                  }}
                >
                  {priceDefinitionsCode}
                </SyntaxHighlighter>
              </div>
            </>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    maxWidth: "800px !important",
    width: "800px !important",
    maxHeight: "80vh !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "0px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
    paddingTop: "16px",
  },

  dialog__tabsContainer: {
    padding: "8px 16px 0px 16px",
    borderBottom: "1px solid #E8E8E8",
  },

  tab: {
    fontSize: "14px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal !important",
    color: "#7A7A7A !important",
    textTransform: "none !important",
    minWidth: "0 !important",
    padding: "6px 12px !important",
    "&.Mui-selected": {
      color: "#0C0E29 !important",
      fontWeight: "bold !important",
    },
  },

  tabIndicator: {
    backgroundColor: "#4654FB !important",
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  textContainer__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "bold",

    color: "#0C0E29",

    margin: "0px 0px 4px 0px",
  },

  textContainer__paragraph: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  textContainer__listTitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 8px 0px",
  },

  textContainer__listItem: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 8px 0px",
  },

  inlineCode: {
    fontFamily: "monospace",
    fontSize: "14px",
    backgroundColor: "#F0F0F0",
    borderRadius: "3px",
    padding: "1px 4px",
    color: "#0C0E29",
  },

  functionExampleContainer: {
    width: "100%",
    boxSizing: "border-box",

    backgroundColor: "#F8F8F8",

    borderRadius: "8px",
    padding: "8px",

    overflow: "hidden",
    position: "relative",

    "& > pre": {
      "&::-webkit-scrollbar": {
        width: "8px" /* Adjust width for vertical scroll */,
        height: "8px" /* Adjust height for horizontal scroll */,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(0, 0, 0, 0.5)" /* A semi-transparent thumb */,
        borderRadius: "4px" /* Rounded corners */,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.7)" /* Darker thumb on hover */,
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent" /* Make the track transparent */,
      },
    },
  },

  copyButton: {
    position: "absolute",
    top: "8px",
    right: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    border: "none",
    borderRadius: "4px",
    backgroundColor: "#EDEDED",

    color: "#0C0E29",

    padding: "4px 8px",

    transition: "background-color ease-in-out 0.2s",

    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#DCDCDC",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },

    "& p": {
      margin: 0,
    },
  },

  copied: {
    opacity: 0,
  },

  copiedText: {
    opacity: 1,
    color: "#0C0E29",
    position: "absolute",
  },
}));
